import * as React from 'react';

import {
  enumInventoryLotSortOption,
  enumLotStatus,
  InventoryLotSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  Pagination,
  SearchField,
  Select,
  Button,
  ListScreenLayout,
  type ListStatsCardProps,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucidePackage,
  LucideCheckCircle,
  LucideXCircle,
  LucidePlus,
} from 'lucide-react';

import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/inventoryLotsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLotRecord } from '../../types/inventoryLot';

import { LotDialog } from './LotDialog';
import { useInventoryLotsColumns } from './useInventoryLotsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: InventoryLotSortOption;
    desc: InventoryLotSortOption;
  }
> = {
  lotNumber: {
    asc: enumInventoryLotSortOption.LotNumberAsc,
    desc: enumInventoryLotSortOption.LotNumberDesc,
  },
  createdAt: {
    asc: enumInventoryLotSortOption.CreatedAtAsc,
    desc: enumInventoryLotSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ INVENTORY LOTS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const InventoryLotsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteInventoryLots,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const lots = data?.lots ?? [];
  const stats = data?.stats ?? {
    totalLots: 0,
    activeLots: 0,
    closedLots: 0,
  };
  const total = data?.totalCount ?? 0;
  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  // Memoized values
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );
  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.lotsListView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );
  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.lotsListView.filters.allStatuses },
      { key: enumLotStatus.Active, label: t.lotsListView.statusTypes.Active },
      { key: enumLotStatus.Closed, label: t.lotsListView.statusTypes.Closed },
      { key: enumLotStatus.Merged, label: t.lotsListView.statusTypes.Merged },
    ],
    [t],
  );
  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'totalLots',
        label: t.lotsListView.summary.totalLots,
        value: stats.totalLots,
        icon: <LucidePackage />,
        color: 'primary',
      },
      {
        key: 'activeLots',
        label: t.lotsListView.summary.activeLots,
        value: stats.activeLots,
        icon: <LucideCheckCircle />,
        color: 'success',
      },
      {
        key: 'closedLots',
        label: t.lotsListView.summary.closedLots,
        value: stats.closedLots,
        icon: <LucideXCircle />,
        color: 'failure',
      },
    ],
    [stats, t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInventoryLotSortOption.LotNumberAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleRowClick = React.useCallback(
    ({ row }: { row: InventoryLotRecord }) => {
      if (!row?.id) return;
      setLocation(`/inventory/lots/${row.id}`);
    },
    [setLocation],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);
  const handleDialogClose = React.useCallback(() => {
    setIsCreateDialogOpen(false);
  }, [setIsCreateDialogOpen]);
  const handleDialogSuccess = React.useCallback(() => {
    refetch();
    handleDialogClose();
  }, [refetch, handleDialogClose]);

  const columns = useInventoryLotsColumns({ onRowClick: handleRowClick });

  const emptyPanelProps = {
    title: t.lotsListView.emptyState.title,
    description: <span>{t.lotsListView.emptyState.subtitle}</span>,
    centered: true,
  };

  return (
    <div className="@InventoryLotsListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.lotsListView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedFacility || 'all'}
              onChange={(key) => {
                setSelectedFacility(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={facilityOptions}
              className="w-48"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={(key) => {
                setSelectedStatus(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={statusOptions}
              className="w-36"
            />
          </>
        }
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleCreateClick}
          >
            {t.lotsListView.createLot}
          </Button>
        }
        stats={statsColumns}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<InventoryLotRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={lots}
          rowKey="id"
          aria-label={t.lotsListView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.lotsListView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <LotDialog
        isOpen={isCreateDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingLot={null}
      />
    </div>
  );
};
